import { useAchievements } from "api/achievements";
import { useAppSelector } from "app/hooks";
import {
  DynamicSearch,
  DynamicSearchType,
} from "components/FiltersV2/DynamicSearch";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { createHeadItems, OLTable } from "components/Table/OLTable";
import { StatusValue } from "components/Table/StatusValue";
import { calculateCount } from "components/Table/tableHelpers";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useEffect, useState } from "react";
import { selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { GLOBAL_MANAGEMENT_ACHIEVEMENTS_LIST_ID } from "state/tables/types";
import { AchievementListItem } from "types/achievements";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ListingActions } from "types/listing";
import { OLDate } from "utilities/dateFormat";
import { Stores } from "../Stores/Stores";
import { TableProps } from "./Wallets";

export const AchievementsTable = ({
  rowsSelected,
  selectRow,
  unselectRow,
  selectAll,
  unselectAll,
  clearSelection,
  selectionDisabled,
}: TableProps) => {
  const tables = useAppSelector(selectTables);
  const { message } = useMessage();
  const { listing, updateListing } = useListing(
    GLOBAL_MANAGEMENT_ACHIEVEMENTS_LIST_ID,
  );
  const [dynamicSearch, setDynamicSearch] = useState<DynamicSearchType>();
  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[GLOBAL_MANAGEMENT_ACHIEVEMENTS_LIST_ID]?.filters?.filter(
      (item: SelectedFilter) => item.type !== "dynamic-search",
    ) || [],
  );
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();

  const setSelectedFilters = (filters: SelectedFilterV2[]) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      GLOBAL_MANAGEMENT_ACHIEVEMENTS_LIST_ID,
    );

    setFilters(filters);
  };
  const { useAchievementList } = useAchievements();
  const { data, error } = useAchievementList({
    filters: parseFiltersForBE(getNewFiltersOnly(filters)),
    order: listing.order || "desc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "createdAt",
    dynamicSearch,
  });

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && data) {
      setPersistentFilters(filters, GLOBAL_MANAGEMENT_ACHIEVEMENTS_LIST_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, data, error]);

  const items = data?.items
    ? data.items.map((item: AchievementListItem) => ({
        cells: [
          { content: item.name, property: "name" },
          {
            content: <StatusValue status={item.active} />,
            property: "status",
          },
          { content: OLDate(item.createdAt), property: "createdAt" },
        ],
        id: item.achievementId,
        metadata: { disabled: !item.active },
        data: item,
      }))
    : [];

  const headItems = createHeadItems([
    {
      headerText: message({ id: "name" }),
      property: "name",
      isSortable: true,
    },
    {
      headerText: message({ id: "status" }),
      property: "status",
      isSortable: false,
    },
    {
      headerText: message({ id: "createdOn" }),
      property: "createdAt",
      isSortable: true,
    },
  ]);

  return (
    <>
      <Subheader>
        <FiltersV2
          donutLabel={message({ id: "achievements" })}
          savedFilters={getNewFiltersOnly(filters)}
          data-test-id="achievement-list"
          properties={[
            {
              query: "active",
              label: message({ id: "status" }),
              type: "boolean-status",
            },
            {
              query: "createdAt",
              label: message({ id: "createdOn" }),
              type: "datetime",
            },
            {
              query: "name",
              label: message({ id: "name" }),
              type: "achievement-name",
            },
          ]}
          total={data?.total}
          setFilters={setSelectedFilters}
        />
        <SubheaderWrapper alignRight>
          <Stores
            tableName={GLOBAL_MANAGEMENT_ACHIEVEMENTS_LIST_ID}
            clearSelection={clearSelection}
            clearFilters={() => setFilters([])}
          />
          <DynamicSearch property="name" setDynamicSearch={setDynamicSearch} />
        </SubheaderWrapper>
      </Subheader>
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={GLOBAL_MANAGEMENT_ACHIEVEMENTS_LIST_ID}
        data-test-id="achievement-list"
        columns={data && headItems}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(data?.total.filtered, data?.total.all),
          onChangePage: (page) => {
            updateListing({ type: ListingActions.CHANGE_PAGE, payload: page });
          },
          onChangeRowsPerPage: (rows) => {
            updateListing({
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
              payload: rows,
            });
          },
          page: listing.page,
          rowsPerPage: listing.rowsPerPage,
        }}
        rows={data && items}
        sort={{
          order: listing.order || "desc",
          property: listing.property || "createdAt",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        clearFilters={() => setFilters([])}
        selectable={{
          rowsSelected,
          selectRow,
          unselectRow,
          selectAll,
          unselectAll,
          disabled: selectionDisabled,
          clearSelection,
        }}
      />
    </>
  );
};
